import { useCallback, useMemo } from "react"

import {
  TRACKING_EVENTS,
  useAnalytics,
  type TrackingEventName,
} from "@/lib/analytics"

import type { SofiaActionKind } from "../model/constants"

/**
 * Coarse failure-mode classes for `copilot_action_failed`, so the PostHog error
 * funnel can split failures by cause instead of every event landing with a null
 * reason (the state that made the observed 8x action-failure spike undiagnosable).
 * - `execution_error` — the executor returned `{ ok: false }` with a receipt error.
 * - `exception` — the executor threw before returning a result.
 */
type CoviActionFailureReason = "execution_error" | "exception"

/** How an action crossed its confirmation gate. */
export type CoviActionConfirmationVia = "manual" | "typed" | "auto_execute"

/**
 * Sofia usage analytics — the single place every panel-usage event is
 * emitted. Wraps `useAnalytics().trackEvent`, always stamps
 * legacy `surface: "covi"` so existing PostHog queries keep resolving, and adds
 * `assistant_brand: "sofia"` as the canonical post-rename dimension.
 *
 * PII rule: callers pass only counts/lengths/enums — NEVER message text or
 * prompts. The lengths here mirror `copilot_message_feedback`'s `message_length`.
 *
 * Each track* function maps to one `TRACKING_EVENTS.COPILOT.*` event; the
 * payload shapes are deliberately small and high-signal so the PostHog usage
 * dashboard (DAU/WAU, sessions, messages-per-session, action funnel, error
 * rate) can be built without further instrumentation.
 */
interface CoviAnalytics {
  /** Panel surfaced. `via` records HOW it opened. */
  trackPanelOpened: (
    via: "toggle" | "shortcut" | "auto_open" | "prompt",
  ) => void
  /** Panel hidden. `via` records HOW it closed. */
  trackPanelClosed: (via: "close_button" | "shortcut") => void
  /** A user turn was sent. `messageLength` only — never the text. */
  trackMessageSent: (props: {
    messageLength: number
    via: "composer" | "slash_command" | "history_recall" | "seed"
  }) => void
  /** A completed assistant turn. `latencyMs` is send→reply wall time. */
  trackReplyReceived: (props: {
    latencyMs: number
    replyLength: number
    actionCount: number
  }) => void
  /** A failed assistant turn (HTTP error, empty reply, or transport throw). */
  trackReplyFailed: (props: { latencyMs: number; reason: string }) => void
  /** Sofia proposed one action card. Flags break the funnel down by risk class. */
  trackActionProposed: (props: {
    kind: SofiaActionKind
    requiresConfirmation: boolean
    autoExecute: boolean
    autoConfirmable: boolean
    destructive: boolean
    modify: boolean
  }) => void
  /** A proposed action was confirmed (manually clicked or drive auto-confirmed). */
  trackActionConfirmed: (props: {
    kind: SofiaActionKind
    via: CoviActionConfirmationVia
    serverExecuted: boolean
  }) => void
  /** A confirmed action executed successfully. */
  trackActionExecuted: (props: {
    kind: SofiaActionKind
    via: CoviActionConfirmationVia
    serverExecuted: boolean
  }) => void
  /**
   * A confirmed action failed to execute. `reason` classifies the failure mode
   * and `error` carries a caller-sanitized error message so the failure is
   * diagnosable in PostHog without leaking user input or server diagnostics.
   */
  trackActionFailed: (props: {
    kind: SofiaActionKind
    via: CoviActionConfirmationVia
    serverExecuted: boolean
    reason: CoviActionFailureReason
    error?: string
  }) => void
  /** A proposed action was dismissed without running. */
  trackActionDismissed: (kind: SofiaActionKind) => void
  /** A speed-dial slash command was applied to the composer. */
  trackSlashCommandUsed: (command: string) => void
  /** The user switched to an existing thread from the thread list. */
  trackConversationLoaded: () => void
  /** A thread was deleted from the thread list. */
  trackConversationDeleted: () => void
}

export function useCoviAnalytics(): CoviAnalytics {
  const { trackEvent } = useAnalytics()

  const track = useCallback(
    (event: TrackingEventName, props?: Record<string, unknown>) => {
      trackEvent(event, {
        surface: "covi",
        assistant_brand: "sofia",
        ...props,
      })
    },
    [trackEvent],
  )

  return useMemo<CoviAnalytics>(
    () => ({
      trackPanelOpened: (via) =>
        track(TRACKING_EVENTS.COPILOT.PANEL_OPENED, { via }),
      trackPanelClosed: (via) =>
        track(TRACKING_EVENTS.COPILOT.PANEL_CLOSED, { via }),
      trackMessageSent: ({ messageLength, via }) =>
        track(TRACKING_EVENTS.COPILOT.MESSAGE_SENT, {
          message_length: messageLength,
          via,
        }),
      trackReplyReceived: ({ latencyMs, replyLength, actionCount }) =>
        track(TRACKING_EVENTS.COPILOT.REPLY_RECEIVED, {
          latency_ms: latencyMs,
          reply_length: replyLength,
          action_count: actionCount,
        }),
      trackReplyFailed: ({ latencyMs, reason }) =>
        track(TRACKING_EVENTS.COPILOT.REPLY_FAILED, {
          latency_ms: latencyMs,
          reason,
        }),
      trackActionProposed: ({
        kind,
        requiresConfirmation,
        autoExecute,
        autoConfirmable,
        destructive,
        modify,
      }) =>
        track(TRACKING_EVENTS.COPILOT.ACTION_PROPOSED, {
          kind,
          requires_confirmation: requiresConfirmation,
          auto_execute: autoExecute,
          auto_confirmable: autoConfirmable,
          destructive,
          modify,
        }),
      trackActionConfirmed: ({ kind, via, serverExecuted }) =>
        track(TRACKING_EVENTS.COPILOT.ACTION_CONFIRMED, {
          kind,
          via,
          server_executed: serverExecuted,
        }),
      trackActionExecuted: ({ kind, via, serverExecuted }) =>
        track(TRACKING_EVENTS.COPILOT.ACTION_EXECUTED, {
          kind,
          via,
          server_executed: serverExecuted,
        }),
      trackActionFailed: ({ kind, via, serverExecuted, reason, error }) =>
        track(TRACKING_EVENTS.COPILOT.ACTION_FAILED, {
          kind,
          via,
          server_executed: serverExecuted,
          reason,
          error,
        }),
      trackActionDismissed: (kind) =>
        track(TRACKING_EVENTS.COPILOT.ACTION_DISMISSED, { kind }),
      trackSlashCommandUsed: (command) =>
        track(TRACKING_EVENTS.COPILOT.SLASH_COMMAND_USED, { command }),
      trackConversationLoaded: () =>
        track(TRACKING_EVENTS.COPILOT.CONVERSATION_LOADED),
      trackConversationDeleted: () =>
        track(TRACKING_EVENTS.COPILOT.CONVERSATION_DELETED),
    }),
    [track],
  )
}
